'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ProcessStep {
  id: number;
  phase: string;
  title: string;
  description: string;
  icon: string;
  duration: string;
  deliverables: string[];
}

interface ProcessTimelineProps {
  className?: string;
}

const ProcessTimeline = ({ className = '' }: ProcessTimelineProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeStep, setActiveStep] = useState(1);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const processSteps: ProcessStep[] = [
    {
      id: 1,
      phase: 'Fase 1',
      title: 'AI-Readiness Assessment',
      description: 'We analyseren jouw huidige processen en identificeren de beste AI-kansen voor jouw bedrijf.',
      icon: 'MagnifyingGlassIcon',
      duration: '1-2 weken',
      deliverables: ['Procesanalyse rapport', 'ROI-berekening', 'Implementatie roadmap'],
    },
    {
      id: 2,
      phase: 'Fase 2',
      title: 'Strategie & Planning',
      description: 'Samen ontwikkelen we een op maat gemaakte AI-strategie die aansluit bij jouw bedrijfsdoelen.',
      icon: 'MapIcon',
      duration: '1 week',
      deliverables: ['AI-strategie document', 'Technische architectuur', 'Project planning'],
    },
    {
      id: 3,
      phase: 'Fase 3',
      title: 'Ontwikkeling & Integratie',
      description: 'Onze experts bouwen en integreren de AI-oplossingen naadloos in jouw bestaande systemen.',
      icon: 'CodeBracketIcon',
      duration: '4-8 weken',
      deliverables: ['Custom AI-modellen', 'Systeem integraties', 'API-koppelingen'],
    },
    {
      id: 4,
      phase: 'Fase 4',
      title: 'Training & Optimalisatie',
      description: 'We trainen jouw team en optimaliseren de AI-systemen voor maximale prestaties.',
      icon: 'AcademicCapIcon',
      duration: '2 weken',
      deliverables: ['Team training', 'Documentatie', 'Performance tuning'],
    },
    {
      id: 5,
      phase: 'Fase 5',
      title: 'Launch & Monitoring',
      description: 'We lanceren de oplossing en monitoren continu de prestaties voor blijvende verbetering.',
      icon: 'RocketLaunchIcon',
      duration: 'Doorlopend',
      deliverables: ['Go-live support', 'Performance dashboards', 'Maandelijkse rapportages'],
    },
  ];

  return (
    <section className={`py-16 lg:py-24 bg-gradient-to-b from-[#0a0a0a] to-background ${className}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-8 lg:mb-12">
          <div className="inline-flex items-center space-x-2 glassmorphic px-4 py-2 rounded-full mb-6 shadow-silver-glow animate-fade-in-up card-3d-glow">
            <Icon name="ClipboardDocumentCheckIcon" size={16} className="text-primary" />
            <span className="text-sm font-medium text-foreground">Onze Aanpak</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            Van idee tot{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent">
              werkende AI-oplossing
            </span>
          </h2>
          <p className="text-lg text-muted-foreground animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Onze bewezen 5-fasen methodologie zorgt voor een soepele implementatie met meetbare resultaten in elke stap.
          </p>
        </div>

        {/* Expanding Cards — Desktop */}
        <div className="hidden lg:flex items-stretch gap-3 w-full" style={{ minHeight: '480px' }}>
          {processSteps.map((step, index) => {
            const isActive = activeStep === step.id;
            return (
              <div
                key={step.id}
                onMouseEnter={() => isHydrated && setActiveStep(step.id)}
                className="relative cursor-pointer"
                style={{
                  flex: isActive ? '5 0 0%' : '1 0 0%',
                  transition: 'flex 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  minWidth: 0,
                }}
              >
                {/* Card body — full height, number inside at top */}
                <div
                  className={`relative glassmorphic rounded-2xl overflow-hidden h-full transition-all duration-500 ${
                    isActive
                      ? 'border-2 border-primary shadow-orange-glow-hover'
                      : 'border border-primary/15 shadow-silver-glow'
                  }`}
                  style={{ minHeight: '480px' }}
                >
                  {/* Background glow when active */}
                  {isActive && (
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent pointer-events-none" />
                  )}

                  {/* ── Collapsed view ── */}
                  <div
                    className={`absolute inset-0 flex flex-col items-center pt-6 gap-3 transition-opacity duration-400 ${
                      isActive ? 'opacity-0 pointer-events-none' : 'opacity-100'
                    }`}
                  >
                    {/* Step number — inside card at top */}
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a] border-2 border-primary/40 flex items-center justify-center flex-shrink-0">
                      <span className="text-base font-bold font-display text-primary">{step.id}</span>
                    </div>

                    {/* Icon */}
                    <div className="w-11 h-11 rounded-full glassmorphic border border-primary/30 flex items-center justify-center flex-shrink-0">
                      <Icon name={step.icon as any} size={20} className="text-primary" />
                    </div>

                    {/* Phase label */}
                    <span className="text-xs font-mono text-primary font-bold tracking-wider text-center px-2">
                      {step.phase}
                    </span>

                    {/* Title — always visible, rotated for collapsed narrow cards */}
                    <div className="flex-1 flex items-center justify-center overflow-hidden px-1">
                      <span
                        className="text-xs font-display font-semibold text-foreground/70 text-center leading-tight"
                        style={{
                          writingMode: 'vertical-rl',
                          textOrientation: 'mixed',
                          transform: 'rotate(180deg)',
                          maxHeight: '140px',
                          overflow: 'hidden',
                        }}
                      >
                        {step.title}
                      </span>
                    </div>
                  </div>

                  {/* ── Expanded view ── */}
                  <div
                    className={`absolute inset-0 p-6 flex flex-col gap-4 transition-opacity duration-400 ${
                      isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    }`}
                  >
                    {/* Step number — inside card at top */}
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-3d-hover flex-shrink-0">
                        <span className="text-xl font-bold font-display text-background">{step.id}</span>
                      </div>
                      <div>
                        <span className="text-xs font-mono text-primary font-bold tracking-wider block">{step.phase}</span>
                        <h3 className="text-base font-display font-bold text-foreground leading-tight">{step.title}</h3>
                      </div>
                    </div>

                    {/* Icon row */}
                    <div className="flex items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-3d-hover flex-shrink-0">
                        <Icon name={step.icon as any} size={18} className="text-background" />
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>

                    <div className="flex items-center space-x-2 text-xs text-muted-foreground bg-[#0a0a0a]/50 rounded-lg px-3 py-2 w-fit">
                      <Icon name="ClockIcon" size={13} className="text-accent" />
                      <span className="font-semibold">{step.duration}</span>
                    </div>

                    {/* Deliverables */}
                    <div className="space-y-2 pt-2 border-t border-primary/20 flex-1">
                      <p className="text-xs font-bold text-primary tracking-wide">DELIVERABLES:</p>
                      <ul className="space-y-2">
                        {step.deliverables.map((deliverable, idx) => (
                          <li key={idx} className="flex items-start space-x-2 text-xs text-muted-foreground">
                            <div className="flex-shrink-0 w-4 h-4 rounded-full bg-success/20 flex items-center justify-center mt-0.5">
                              <Icon name="CheckIcon" size={10} className="text-success" />
                            </div>
                            <span className="leading-relaxed">{deliverable}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Timeline - Mobile/Tablet */}
        <div className="lg:hidden space-y-8">
          {processSteps.map((step, index) => (
            <div key={step.id} className="relative animate-fade-in-up" style={{ animationDelay: `${0.3 + index * 0.1}s` }}>
              {index < processSteps.length - 1 && (
                <div className="absolute left-10 top-24 bottom-0 w-1 bg-gradient-to-b from-primary via-accent to-primary shadow-orange-glow">
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 animate-bounce">
                    <Icon name="ChevronDownIcon" size={20} className="text-primary drop-shadow-[0_0_8px_rgba(255,102,0,0.8)]" />
                  </div>
                </div>
              )}

              <div className="flex space-x-4">
                <div className="flex-shrink-0 flex flex-col items-center space-y-3">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-silver-glow relative animate-float-3d card-3d-glow">
                    <span className="text-2xl font-bold font-display text-background">{step.id}</span>
                    <div className="absolute inset-0 rounded-full border-2 border-primary/50 animate-spin-slow"></div>
                  </div>
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-silver-glow card-3d-glow">
                    <Icon name={step.icon as any} size={24} className="text-background" />
                  </div>
                </div>

                <div className="flex-1 glassmorphic rounded-2xl p-6 shadow-silver-glow hover:shadow-orange-glow-hover card-3d-glow border border-primary/20 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-2xl"></div>
                  <div className="space-y-4 relative z-10">
                    <div>
                      <span className="text-xs font-mono text-primary font-bold tracking-wider">{step.phase}</span>
                      <h3 className="text-xl font-display font-bold text-foreground mt-2 leading-tight">{step.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground bg-[#0a0a0a]/50 rounded-lg px-3 py-2 w-fit">
                      <Icon name="ClockIcon" size={14} className="text-accent" />
                      <span className="font-semibold">{step.duration}</span>
                    </div>
                    <div className="space-y-2 pt-3 border-t border-primary/20">
                      <p className="text-xs font-bold text-primary tracking-wide">DELIVERABLES:</p>
                      <ul className="space-y-2">
                        {step.deliverables.map((deliverable, idx) => (
                          <li key={idx} className="flex items-start space-x-2 text-xs text-muted-foreground">
                            <div className="flex-shrink-0 w-4 h-4 rounded-full bg-success/20 flex items-center justify-center mt-0.5">
                              <Icon name="CheckIcon" size={10} className="text-success" />
                            </div>
                            <span className="leading-relaxed">{deliverable}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12 lg:mt-16 animate-fade-in-up" style={{ animationDelay: '0.8s' }}>
          <p className="text-muted-foreground mb-6">
            Klaar om te starten met jouw AI-transformatie?
          </p>
          <a
            href="/contact"
            className="inline-flex items-center space-x-2 px-8 py-4 rounded-lg font-semibold text-lg bg-cta text-cta-foreground hover:bg-cta/90 transition-all duration-300 shadow-silver-glow hover:shadow-orange-glow-hover hover:-translate-y-2 button-3d group"
          >
            <span>Plan een gratis consultatie</span>
            <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProcessTimeline;